/**
 * RequestCard Component
 *
 * Individual card display for an advisory request
 */

import React from 'react';

import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { Box } from '@mui/material';

import StatusBadge from './StatusBadge';
import * as S from './styles';
import UserDisplay from './UserDisplay';
import Button from 'components/Button';
import Text from 'components/Text';
import { LeadRequestWithUserData } from 'types/leadRequestsTypes';

interface RequestCardProps {
  request: LeadRequestWithUserData;
  onViewDetails: (request: LeadRequestWithUserData) => void;
  onCreateLink: (request: LeadRequestWithUserData) => void;
}

const RequestCard: React.FC<RequestCardProps> = ({ request, onViewDetails, onCreateLink }) => {
  const createdAt = new Date(request.created_at);
  const timeAgo = formatDistanceToNow(createdAt, { addSuffix: true, locale: ptBR });

  // Get first few fields from form_responses for preview
  const getFormPreview = (): string => {
    if (!request.form_responses) return 'Nenhum dado do formulário';

    if (typeof request.form_responses === 'object' && !Array.isArray(request.form_responses)) {
      const entries = Object.entries(request.form_responses).slice(0, 2);
      return entries.map(([key, value]) => `${key}: ${value}`).join(' | ') || 'Nenhum dado';
    }

    return 'Dados do formulário disponíveis';
  };

  const hasInvitationLinks = request.invitation_links && request.invitation_links.length > 0;

  return (
    <S.RequestCard onClick={() => onViewDetails(request)}>
      <S.CardHeader>
        <S.CardTitle>
          <Text size="large" bold>
            Solicitação #{request.id.slice(0, 8)}
          </Text>
          <StatusBadge status={request.status} size="small" />
        </S.CardTitle>
      </S.CardHeader>

      <S.CardBody>
        <S.InfoRow>
          <S.InfoLabel>Solicitante:</S.InfoLabel>
        </S.InfoRow>
        <Box mb={2}>
          <UserDisplay userData={request.requester_user_data} size="small" />
        </Box>

        {/* {request.lead_user_data && (
          <>
            <S.InfoRow>
              <S.InfoLabel>Lead:</S.InfoLabel>
            </S.InfoRow>
            <Box mb={2}>
              <UserDisplay userData={request.lead_user_data} size="small" />
            </Box>
          </>
        )} */}

        <S.InfoRow>
          <S.InfoLabel>Visualização:</S.InfoLabel>
          <S.InfoValue>{getFormPreview()}</S.InfoValue>
        </S.InfoRow>

        {hasInvitationLinks && (
          <S.InfoRow>
            <S.InfoLabel>Links de convite:</S.InfoLabel>
            <S.InfoValue>{request.invitation_links!.length} criados</S.InfoValue>
          </S.InfoRow>
        )}
      </S.CardBody>

      <S.CardFooter>
        <Text size="small" color="textSecondary">
          Criado {timeAgo}
        </Text>

        <S.CardActions onClick={e => e.stopPropagation()}>
          <Button variant="outline" size="XSmall" onClick={() => onViewDetails(request)}>
            Ver detalhes
          </Button>
          <Button variant="primary" size="XSmall" onClick={() => onCreateLink(request)}>
            Criar link
          </Button>
        </S.CardActions>
      </S.CardFooter>
    </S.RequestCard>
  );
};

export default RequestCard;
